import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permission";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MdCheck, MdRemove } from "react-icons/md";
import { RowButton, StatusChip } from "../../components/mui";

/**
 * 帳號與角色管理（設計稿 #screen-account，el-pm-template_4.html:2304-2373）
 *
 * 舊版這一頁是一張寬表格：大頭像、姓名＠帳號、Email、部門·職稱、角色膠囊、
 * 2FA 狀態、最後登入、一顆 22px 的撥桿、再一顆 34px 的鉛筆圖示鈕。
 * 十個欄位裡有四個後端根本沒有（部門、職稱、2FA、最後登入），畫面上卻
 * 一路顯示「—」與「2FA 未設定」，看起來像每個人都沒設定，其實是系統沒有這個功能。
 *
 * 新版只畫後端真的回得出來的東西（硬規則 10）。/Auth/Admin/List 的 Select
 * 就只有這七個欄位：account、name、email、activation、role、create_at、update_at。
 *
 * ── 整段拿掉、後端補上才畫回來的欄位（NONE-AVAILABLE）──────────────
 *   · 上次登入 —— **舊版拿 update_at 冒充**（`_.get(a,"last_login", … "update_at")`）。
 *     update_at 是「這筆資料上次被改的時間」：管理員幫他改個名字，
 *     畫面上他的「最後登入」就跳成今天。這比沒有還糟，所以整格刪掉。
 *   · 閒置天數、久未登入的警示列（設計稿 L2345-2351）—— 要先有上次登入才算得出來。
 *   · 可看專案數、名下工項數（設計稿 L2331、L2354）—— 後端沒有這兩個統計。
 *   · 離職交接的警示橫幅（設計稿 L2364-2367）—— 同上，而且也沒有批次轉派的端點。
 *   · 2FA、部門、職稱 —— admin 資料表沒有這些欄位。
 *
 * ── 啟用／停用搬到哪去了 ──────────────────────────────────────────
 * 搬到編輯人員頁（/Setting/Admin/:account）的「停用這個帳號」摺疊區。
 * 這裡的每一列整列都是一顆按鈕（設計稿 L2321），列裡面不能再塞一顆按鈕
 * —— 按鈕不能包按鈕，鍵盤也走不進去。
 * 功能一個都沒有少，只是換了位置（硬規則 8）。
 *
 * 整頁只有「新增人員」是 contained 主要按鈕（硬規則 1）。
 */

/** 狀態徽章：使用中 ✓、已停用 ―（符號與全站一致，見 pages/MyTask/Index.js 的 tagOf） */
function statusTag(active) {
  return active
    ? { kind: "done", mark: <MdCheck />, text: "使用中" }
    : { kind: "closed", mark: <MdRemove />, text: "已停用" };
}

/** 篩選列前面的小標（只看：／角色：／找人：） */
const FILTER_LABEL = { component: "label", variant: "body2", color: "text.secondary" };

function SettingAdmin(props) {
  const navigate = useNavigate();
  const { adminList, roleList, canSeeRoles, GET_AdminList, GET_RoleList } = props;

  /** 狀態篩選：""＝全部、"on"＝使用中、"off"＝已停用 */
  const [status, setStatus] = useState("");
  /** 角色篩選：空字串＝全部。存字串，因為 <select> 的 value 一律是字串 */
  const [roleFilter, setRoleFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  /* 「資料回來了沒」要跟「一個人都沒有」分開：混在一起的話，每次進頁面都會
     先閃一下「目前沒有帳號」，管理員會以為資料掉了 */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    GET_AdminList(() => setReady(true), null);
    GET_RoleList(null, null);
  }, [GET_AdminList, GET_RoleList]);

  const roleOptions = useMemo(() => (Array.isArray(roleList) ? roleList : []), [roleList]);

  /**
   * 後端的 role 是單一物件（AdminList 取 FirstOrDefault），不是陣列；
   * 舊版同時吃 roles 陣列是為了相容更早的回應格式，這裡一併留著，
   * 拿不到就是沒有角色 —— 不要退回「一般使用者」這種系統裡不存在的角色名。
   */
  const admins = useMemo(() => {
    const list = Array.isArray(adminList) ? adminList : [];
    return list.map((a) => {
      const account = _.get(a, "account", _.get(a, "Account", "")) || "";
      const one = _.get(a, "role", null);
      const arr = _.get(a, "roles", null);
      const r = one || (Array.isArray(arr) && arr.length ? arr[0] : null);
      return {
        account,
        name: _.get(a, "name", _.get(a, "Name", "")) || account,
        email: _.get(a, "email", _.get(a, "Email", "")) || "",
        roleId: _.get(r, "id", _.get(r, "Id", null)),
        roleName: _.get(r, "name", _.get(r, "Name", "")) || "",
        active: _.get(a, "activation", _.get(a, "Activation", true)) !== false,
      };
    });
  }, [adminList]);

  const activeCount = useMemo(() => admins.filter((a) => a.active).length, [admins]);

  /** 依狀態 + 角色 + 關鍵字篩選。角色比對用 id，避免同名角色互相干擾 */
  const shown = useMemo(() => {
    const rid = roleFilter ? Number(roleFilter) : null;
    const q = keyword.trim().toLowerCase();
    return admins.filter((a) => {
      if (status === "on" && !a.active) return false;
      if (status === "off" && a.active) return false;
      if (rid != null && a.roleId !== rid) return false;
      if (!q) return true;
      return [a.name, a.account, a.email, a.roleName].some((v) => String(v).toLowerCase().includes(q));
    });
  }, [admins, status, roleFilter, keyword]);

  const filtering = !!(status || roleFilter || keyword.trim());

  return (
    <>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          帳號與角色
        </Typography>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button variant="contained" type="button" onClick={() => navigate("/Setting/Admin/New")}>
          新增人員
        </Button>
        {/* 沒有 role 權限的人點進去只會撞到「你沒有權限」，那一顆就不畫 */}
        {canSeeRoles ? (
          <Button variant="outlined" type="button" onClick={() => navigate("/Setting/Roles")}>
            檢視角色權限表
          </Button>
        ) : null}
      </Stack>

      {/* 數字全部是從回來的清單算的，沒有寫死（設計稿 L2310 那句是範本假資料） */}
      {ready ? (
        <Typography sx={{ mt: 1.5 }}>
          共 {admins.length} 個帳號，其中 {activeCount} 個使用中。停用的帳號無法登入，但其歷史紀錄均會保留。
        </Typography>
      ) : null}

      {/* 篩選列（設計稿 L2312-2318）。搜尋框是舊版就有的功能，人一多就需要，
          照硬規則 8 留著 —— 設計稿只畫兩個下拉是因為它只有 8 筆範本資料 */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", py: 2 }}>
        <Typography {...FILTER_LABEL} htmlFor="acf">
          狀態：
        </Typography>
        <TextField
          select
          id="acf"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          slotProps={{ select: { native: true } }}
        >
          <option value="">全部 {admins.length} 人</option>
          <option value="on">使用中 {activeCount} 人</option>
          <option value="off">已停用 {admins.length - activeCount} 人</option>
        </TextField>

        <Typography {...FILTER_LABEL} htmlFor="acr">
          角色：
        </Typography>
        <TextField
          select
          id="acr"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          slotProps={{ select: { native: true } }}
        >
          <option value="">全部角色</option>
          {roleOptions.map((r) => {
            const id = _.get(r, "id");
            /* 順帶把該角色目前有幾個人標出來，不用先篩了才知道 */
            const n = admins.filter((a) => a.roleId === Number(id)).length;
            return (
              <option key={id} value={id}>
                {_.get(r, "name", "")}（{n}）
              </option>
            );
          })}
        </TextField>

        <Typography {...FILTER_LABEL} htmlFor="acq">
          搜尋：
        </Typography>
        <TextField
          id="acq"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="姓名、帳號或 Email"
          sx={{ minWidth: 220 }}
        />

        {filtering ? (
          <Button
            variant="text"
            type="button"
            onClick={() => {
              setStatus("");
              setRoleFilter("");
              setKeyword("");
            }}
          >
            清除篩選條件
          </Button>
        ) : null}
      </Stack>

      {!ready ? null : shown.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2">{filtering ? "查無符合條件的人員" : "尚無任何帳號"}</Typography>
          {/* 「清掉篩選條件」那顆就在上面幾十像素處，這裡不再放第二顆一模一樣的鈕 */}
          <Typography sx={{ mt: 1 }}>
            {filtering
              ? "請調整篩選條件，或按上方的「清除篩選條件」。"
              : "請按右上角的「新增人員」建立第一個帳號。"}
          </Typography>
        </Box>
      ) : (
        <Card>
          <List disablePadding>
            {shown.map((a) => {
              const tag = statusTag(a.active);
              return (
                <RowButton
                  key={a.account}
                  onClick={() => navigate(`/Setting/Admin/${encodeURIComponent(a.account)}`)}
                  side={
                    <StatusChip kind={tag.kind} mark={tag.mark}>
                      {tag.text}
                    </StatusChip>
                  }
                >
                  <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      {/* 角色徽章是無彩的 type：角色不是狀態，不該跟「已逾期」搶顏色 */}
                      {a.roleName ? <StatusChip kind="type">{a.roleName}</StatusChip> : null}
                      <Typography variant="subtitle1" component="span">
                        {a.name}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="div"
                      sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
                    >
                      {/* Email 沒填就直說沒填，不要留一格空白讓人以為畫面壞了 */}
                      <span>{a.email || "未填寫 Email"}</span>
                      <span>帳號：{a.account}</span>
                      {a.roleName ? null : <span>尚未指派角色</span>}
                    </Typography>
                  </Box>
                </RowButton>
              );
            })}
          </List>
        </Card>
      )}

      {ready && shown.length > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          點選任一列可編輯該使用者的姓名、Email、角色，或停用其帳號。
        </Typography>
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  adminList: _.get(state, "Admin.List", []),
  roleList: _.get(state, "Role.List", []),
  /* 角色權限表那條路由要 role 權限（resources/menu.js 的 set_role）；
     這裡只影響按鈕顯不顯示，真正的把關在後端 */
  canSeeRoles: hasPermission("role"),
});

const mapDispatchToProps = (dispatch) => ({
  GET_AdminList: (callback, loading) => dispatch({ type: "GET_AdminList", callback, loading }),
  GET_RoleList: (callback, loading) => dispatch({ type: "GET_RoleList", callback, loading }),
});

SettingAdmin.propTypes = {
  adminList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  roleList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  canSeeRoles: PropTypes.bool,
  GET_AdminList: PropTypes.func,
  GET_RoleList: PropTypes.func,
};

SettingAdmin.defaultProps = {
  adminList: [],
  roleList: [],
  canSeeRoles: false,
  GET_AdminList: () => {},
  GET_RoleList: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingAdmin);
